/**
 * @file     SlideLesson.tsx
 * @readme   ../README.md
 * @purpose  Slide lesson type -- step-through slides with prev/next navigation, swipe gestures, slide transitions, and dot indicators.
 */
'use client';

import '@/core/i18n/deferred/academy';
import { useState, useRef, useCallback, useEffect } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useAppTheme } from '@/core/theme';
import { microLabelSx } from '@/components/hub/hubKitStyles';
import type { LessonContent } from '@/content-system/academy/types';
import { QuizSection } from '../quiz/QuizSection';
import { LegalDisclaimer } from '../LegalDisclaimer';
import { LessonMarkdown } from '../LessonMarkdown';

interface SlideLessonProps {
  content: LessonContent;
  onContentConsumed?: () => void;
  onQuizPassed?: () => void;
}

export function SlideLesson({ content, onContentConsumed, onQuizPassed }: SlideLessonProps) {
  const { theme } = useAppTheme();
  const { t } = useTranslation();
  const dur = theme.motion.durations;
  const ease = theme.motion.easing;
  const slides = content.slides ?? [];
  const [currentSlide, setCurrentSlide] = useState(0);
  const [direction, setDirection] = useState<'left' | 'right'>('right');
  const [animKey, setAnimKey] = useState(0);
  const touchStartX = useRef<number | null>(null);

  const isLast = currentSlide === slides.length - 1;

  useEffect(() => {
    if (isLast) onContentConsumed?.();
  }, [isLast, onContentConsumed]);

  const goTo = useCallback((index: number) => {
    if (index < 0 || index >= slides.length || index === currentSlide) return;
    setDirection(index > currentSlide ? 'right' : 'left');
    setAnimKey((k) => k + 1);
    setCurrentSlide(index);
  }, [currentSlide, slides.length]);

  const goNext = useCallback(() => goTo(currentSlide + 1), [goTo, currentSlide]);
  const goPrev = useCallback(() => goTo(currentSlide - 1), [goTo, currentSlide]);

  if (slides.length === 0) return null;

  const slide = slides[currentSlide];
  const isFirst = currentSlide === 0;

  // Swipe gesture handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const diff = e.changedTouches[0].clientX - touchStartX.current;
    const threshold = 50;
    if (diff < -threshold && !isLast) goNext();
    else if (diff > threshold && !isFirst) goPrev();
    touchStartX.current = null;
  };

  const enterFrom = direction === 'right' ? 'translateX(24px)' : 'translateX(-24px)';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        '@keyframes slideEnter': {
          from: { opacity: 0, transform: enterFrom },
          to: { opacity: 1, transform: 'translateX(0)' },
        },
        '@media (prefers-reduced-motion: reduce)': {
          '& *': {
            animationDuration: '0ms !important',
          },
        },
      }}
    >
      {/* Slide counter */}
      <Typography sx={microLabelSx}>
        {t('academy.lessonPage.slideCounter', { defaultValue: 'Slide {{n}} of {{total}}', n: currentSlide + 1, total: slides.length })}
      </Typography>

      {/* Slide content with swipe */}
      <Box
        key={animKey}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        sx={{
          // The first slide is server-rendered and never animates in.
          animation: animKey > 0 ? `slideEnter ${dur.normal}ms ${ease.decelerate}` : 'none',
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontFamily: theme.typography.fontFamilyHeading,
            fontWeight: 600,
            color: theme.semanticColors.textPrimary,
            mb: 2,
          }}
        >
          {slide.title}
        </Typography>
        <LessonMarkdown>{slide.body}</LessonMarkdown>
        {slide.tip && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              borderRadius: `${theme.layout.radius.lg}px`,
              backgroundColor: theme.semanticColors.surfaceCard,
              border: `1px solid ${theme.semanticColors.borderSubtle}`,
            }}
          >
            <Typography sx={{ fontSize: 13, color: theme.semanticColors.textSecondary, fontWeight: 500 }}>
              Tip: {slide.tip}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Dot indicators */}
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
        {slides.map((_, i) => (
          <Box
            key={i}
            onClick={() => goTo(i)}
            role="button"
            tabIndex={0}
            aria-label={`Go to slide ${i + 1}`}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') goTo(i); }}
            sx={{
              width: i === currentSlide ? 16 : 8,
              height: 8,
              borderRadius: `${theme.layout.radius.pill}px`,
              backgroundColor: i === currentSlide
                ? theme.semanticColors.interactivePrimary
                : alpha(theme.rawSemanticColors.interactivePrimary, 0.2),
              cursor: 'pointer',
              transition: `all ${dur.fast}ms ${ease.standard}`,
              '&:hover': {
                backgroundColor: i === currentSlide
                  ? theme.semanticColors.interactivePrimary
                  : alpha(theme.rawSemanticColors.interactivePrimary, 0.4),
              },
              '&:focus-visible': {
                outline: `2px solid ${theme.semanticColors.interactivePrimary}`,
                outlineOffset: 2,
              },
            }}
          />
        ))}
      </Box>

      {/* Slide nav */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button
          variant="outlined"
          size="small"
          disabled={isFirst}
          onClick={goPrev}
          aria-label="Previous slide"
          sx={{ textTransform: 'none', borderRadius: `${theme.layout.radius.md}px`, minHeight: 44, minWidth: 52, fontSize: 18, lineHeight: 1 }}
        >
          ←
        </Button>
        <Button
          variant="contained"
          size="small"
          onClick={goNext}
          disabled={isLast}
          aria-label={isLast ? 'Last slide reached' : 'Next slide'}
          sx={{ textTransform: 'none', borderRadius: `${theme.layout.radius.md}px`, minHeight: 44, minWidth: 52, fontSize: 18, lineHeight: 1 }}
        >
          {isLast ? '✓' : '→'}
        </Button>
      </Box>

      {/* Show quiz after last slide */}
      {isLast && content.quiz.length > 0 && <QuizSection questions={content.quiz} lessonId={content.id} onQuizPassed={onQuizPassed} />}

      {/* Legal disclaimer and sources */}
      {isLast && <LegalDisclaimer sources={content.sources} lastVerified={content.lastVerified} />}

      {/* Key takeaways after quiz */}
      {isLast && content.keyTakeaways.length > 0 && (
        <Box
          sx={{
            p: 3,
            borderRadius: `${theme.layout.radius.lg}px`,
            backgroundColor: theme.semanticColors.surfaceCard,
            border: `1px solid ${theme.semanticColors.borderSubtle}`,
          }}
        >
          <Typography sx={{ fontWeight: 600, fontSize: 14, mb: 1 }}>Key Takeaways</Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {content.keyTakeaways.map((t, i) => (
              <Typography component="li" key={i} sx={{ fontSize: 14, color: theme.semanticColors.textSecondary, mb: 0.5 }}>
                {t}
              </Typography>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}
